var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * YearPicker Component
 * Renders a year selection grid
 */
import React, { memo, useCallback, useMemo } from 'react';
import { styles } from './YearPicker.styles';
/**
 * YearPicker - Year selection grid component
 */
export const YearPicker = memo(({ activeDate, selectedDate, config, onYearSelect, minDate, maxDate, columns = 5, rows = 4, }) => {
    const activeYear = activeDate.getFullYear();
    const currentYear = new Date().getFullYear();
    const selectedYear = selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate.getFullYear();
    const totalYears = columns * rows;
    const startYear = activeYear - Math.floor(totalYears / 2);
    // Check if a year is disabled
    const isYearDisabled = useCallback((year) => {
        if (!minDate && !maxDate)
            return false;
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);
        if (minDate && yearEnd < minDate)
            return true;
        if (maxDate && yearStart > maxDate)
            return true;
        return false;
    }, [minDate, maxDate]);
    // Handle year click
    const handleYearClick = useCallback((year) => {
        if (!isYearDisabled(year)) {
            onYearSelect === null || onYearSelect === void 0 ? void 0 : onYearSelect(year);
        }
    }, [isYearDisabled, onYearSelect]);
    // Generate year grid
    const yearGrid = useMemo(() => {
        const grid = [];
        let yearIndex = 0;
        for (let row = 0; row < rows; row++) {
            const rowYears = [];
            for (let col = 0; col < columns; col++) {
                rowYears.push(startYear + yearIndex);
                yearIndex++;
            }
            grid.push(rowYears);
        }
        return grid;
    }, [startYear, rows, columns]);
    // Get year range for display
    const yearRange = useMemo(() => ({
        start: startYear,
        end: startYear + totalYears - 1,
    }), [startYear, totalYears]);
    // Render a year cell
    const renderYear = useCallback((year) => {
        const isDisabled = isYearDisabled(year);
        const isCurrent = year === currentYear;
        const isSelected = year === selectedYear;
        const cellStyle = Object.assign(Object.assign(Object.assign(Object.assign({}, styles.yearCell), (isDisabled ? styles.yearDisabled : {})), (isCurrent ? styles.yearCurrent : {})), (isSelected ? styles.yearSelected : {}));
        return (React.createElement("button", { key: year, style: cellStyle, onClick: () => handleYearClick(year), disabled: isDisabled, "aria-label": `${year}`, "aria-selected": isSelected, "aria-current": isCurrent ? 'date' : undefined, tabIndex: isDisabled ? -1 : 0 }, year));
    }, [currentYear, selectedYear, isYearDisabled, handleYearClick]);
    return (React.createElement("div", { style: styles.container, role: "grid", "aria-label": `Years ${yearRange.start} to ${yearRange.end}` },
        React.createElement("div", { style: styles.rangeLabel },
            yearRange.start,
            " - ",
            yearRange.end),
        React.createElement("div", { style: styles.grid }, yearGrid.map((row, rowIndex) => (React.createElement("div", { key: rowIndex, style: styles.row, role: "row" }, row.map(year => renderYear(year))))))));
});
YearPicker.displayName = 'YearPicker';
export default YearPicker;
//# sourceMappingURL=YearPicker.js.map